import { useTranslation } from '../i18n'
import { useApi } from '../hooks/useApi'
import { FeatureCompletenessCard } from '../components'
import type { ProjectDashboardSummary } from '../types'

function formatDate(dateString: string | null): string {
  if (!dateString) return '-'
  return new Date(dateString).toLocaleDateString()
}

export function ProjectDashboard() {
  const { t } = useTranslation()
  const dashboard = useApi<ProjectDashboardSummary>('/project-dashboard')

  if (dashboard.loading) {
    return (
      <div data-testid="project-dashboard-page">
        <h1 className="page-title">{t('page.projects')}</h1>
        <p>{t('label.loading')}</p>
      </div>
    )
  }

  if (dashboard.error) {
    return (
      <div data-testid="project-dashboard-page">
        <h1 className="page-title">{t('page.projects')}</h1>
        <p style={{ color: 'var(--color-error)' }}>Error: {dashboard.error}</p>
      </div>
    )
  }

  const data = dashboard.data

  return (
    <div data-testid="project-dashboard-page">
      <h1 className="page-title">{t('page.projects')}</h1>

      <div className="grid grid-cols-3" style={{ marginTop: '1.5rem', marginBottom: '1.5rem' }}>
        <div className="card stat-card" data-testid="stat-total-hours">
          <span className="stat-value">{data?.totalHours.toFixed(1) ?? 0}h</span>
          <span className="stat-label">{t('label.totalHours')}</span>
        </div>
        <div className="card stat-card" data-testid="stat-project-count">
          <span className="stat-value">{data?.projects.length ?? 0}</span>
          <span className="stat-label">{t('page.projects')}</span>
        </div>
        <div className="card stat-card" data-testid="stat-active-projects">
          <span className="stat-value">{data?.activeProjectCount ?? 0}</span>
          <span className="stat-label">{t('label.activeProjects')}</span>
        </div>
      </div>

      <div className="card">
        <h3>{t('label.projectOverview')}</h3>
        {data?.projects.length === 0 ? (
          <p>{t('empty.noProjects')}</p>
        ) : (
          <table className="table" data-testid="project-table">
            <thead>
              <tr>
                <th>{t('label.project')}</th>
                <th>{t('label.startDate')}</th>
                <th>{t('label.hours')}</th>
                <th>{t('label.totalTests')}</th>
                <th>{t('label.effortToFinish')}</th>
                <th>{t('label.endDate')}</th>
              </tr>
            </thead>
            <tbody>
              {data?.projects.map((project) => (
                <tr key={project.id} data-testid={`project-row-${project.id}`}>
                  <td>
                    <strong>{project.name}</strong>
                    {project.description && (
                      <span style={{ display: 'block', fontSize: '0.875rem', color: '#666' }}>
                        {project.description}
                      </span>
                    )}
                  </td>
                  <td>{formatDate(project.startDate)}</td>
                  <td>{project.totalHours.toFixed(1)}h</td>
                  <td>{project.testCount}</td>
                  <td>
                    {project.projectedEffortToFinish > 0
                      ? `${project.projectedEffortToFinish.toFixed(1)}h`
                      : '-'}
                  </td>
                  <td>{formatDate(project.estimatedEndDate)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {data && data.projects.length > 0 && (
        <div style={{ marginTop: '1.5rem' }}>
          <h3>{t('label.featureCompleteness')}</h3>
          <div
            className="grid"
            style={{
              gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
              gap: '1rem',
            }}
            data-testid="feature-completeness-grid"
          >
            {data.projects.map((project) => (
              <FeatureCompletenessCard key={project.id} project={project} />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
